import { SUPABASE_PUBLISHABLE_KEY } from '../../lib/supabase';
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { fetchWithTimeout, bookSearchHeaders } from '../../lib/bookSearch';

// Pulling someone's newsletter means fetching and parsing a feed on a host we
// do not run. Longer than a search box, but it still has to end.
const FEED_TIMEOUT_MS = 15000;
import { parseNewsletterUrl, safeHttpUrl } from '../../lib/newsletterUrl';
import { useThoughtsFeed, useThoughtComments, useToggleLike, useIsMobile } from '../../hooks/useReaderCorner';
import ReportModal, { KebabMenu, KebabItem } from '../community/ReportModal';

// ─── Brand tokens — DO NOT drift these. Hot takes are ember/coral, never green. ───
const C = { rust: '#B85C38', forest: '#2D4A3E', cream: '#F5F0E8', gold: '#d4a84b', coral: '#E8603C', grey: '#8B7355', ink: '#292524' };
const EMBER = 'linear-gradient(160deg, #6b2418, #3f130e)';

function Avatar({ username, avatar_url, size = 24, ring = '#fff' }) {
  const navigate = useNavigate();
  const initial = (username || '?').slice(0, 1).toUpperCase();
  return (
    <button
      onClick={() => username && navigate(`/u/${username}`)}
      title={username ? `View @${username}` : ''}
      style={{
        width: size, height: size, borderRadius: 'var(--radius-circle)', flexShrink: 0,
        background: avatar_url ? `url(${avatar_url}) center/cover` : C.rust,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: size * 0.42, fontWeight: 700, color: 'var(--brand-white)', fontFamily: 'var(--font-display)',
        border: `2px solid ${ring}`, cursor: username ? 'pointer' : 'default', padding: 'var(--space-none)',
      }}
    >
      {!avatar_url && initial}
    </button>
  );
}

function Handle({ username, color = C.forest }) {
  const navigate = useNavigate();
  if (!username) return null;
  return (
    <button
      onClick={() => navigate(`/u/${username}`)}
      style={{ background: 'none', border: 'none', padding: 'var(--space-none)', fontSize: 'var(--font-size-xs)', color, fontWeight: 700, cursor: 'pointer' }}
    >
      @{username}
    </button>
  );
}

function PostMeta({ postId, likeCount, commentCount, onOpenComments, onDarkCard = false }) {
  const { count, liked, toggle } = useToggleLike(postId, likeCount);
  return (
    <div style={{
      display: 'flex', gap: 18, justifyContent: 'center', marginTop: 'var(--space-md)', paddingTop: 'var(--space-10)',
      borderTop: `1px solid ${onDarkCard ? 'rgba(245,240,232,0.2)' : C.gold + '44'}`,
    }}>
      <span onClick={toggle} style={{ fontSize: 12.5, cursor: 'pointer', color: liked ? C.coral : (onDarkCard ? `${C.cream}99` : C.grey) }}>
        {liked ? '❤️' : '🤍'} {count}
      </span>
      <button onClick={onOpenComments} style={{ background: 'none', border: 'none', padding: 'var(--space-none)', fontSize: 12.5, color: onDarkCard ? `${C.cream}99` : C.grey, cursor: 'pointer' }}>
        💬 {commentCount}
      </button>
    </div>
  );
}

function HotTakeCard({ item, onReport, onOpenComments }) {
  return (
    <div style={{
      background: EMBER, border: `1px solid ${C.coral}55`, borderRadius: '26px 26px 14px 14px',
      boxShadow: '0 8px 24px rgba(63,19,14,0.28)', padding: '18px 20px 15px',
      breakInside: 'avoid', marginBottom: 22, position: 'relative',
    }}>
      <div style={{ position: 'absolute', top: 10, right: 12 }}>
        <KebabMenu dark>
          <KebabItem onClick={() => onReport(item)}>🚩 Report</KebabItem>
        </KebabMenu>
      </div>
      <p style={{ fontSize: 'var(--font-size-tiny)', fontWeight: 700, letterSpacing: 1.3, textTransform: 'uppercase', color: C.coral, margin: '0 0 9px', textAlign: 'center', whiteSpace: 'nowrap' }}>
        🔥 a hot take, served hot
      </p>
      <p style={{ fontFamily: 'var(--font-display)', fontSize: 16.5, fontStyle: 'italic', color: C.cream, lineHeight: 1.4, margin: 'var(--space-none)', textAlign: 'center' }}>
        <span style={{ fontSize: 30, color: C.gold, lineHeight: 0, verticalAlign: '-9px', marginRight: 'var(--space-xs)' }}>"</span>
        {item.text}
        <span style={{ fontSize: 30, color: C.gold, lineHeight: 0, verticalAlign: '-16px', marginLeft: 'var(--space-xs)' }}>"</span>
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, justifyContent: 'center', marginTop: 'var(--space-10)' }}>
        <Avatar username={item.username} avatar_url={item.avatar_url} size={22} ring={C.coral} />
        <Handle username={item.username} color={C.cream} />
      </div>
      <PostMeta postId={item.id} likeCount={item.like_count} commentCount={item.comment_count} onOpenComments={() => onOpenComments(item)} onDarkCard />
    </div>
  );
}

function ThoughtCard({ item, onReport, onOpenComments }) {
  return (
    <div style={{
      background: '#fffdf8', border: `1px solid ${C.gold}66`, borderRadius: '26px 26px 14px 14px',
      boxShadow: '0 5px 20px rgba(45,74,62,0.07)', padding: '18px 20px 15px',
      breakInside: 'avoid', marginBottom: 22, position: 'relative',
    }}>
      <div style={{ position: 'absolute', top: 10, right: 12 }}>
        <KebabMenu>
          <KebabItem onClick={() => onReport(item)}>🚩 Report</KebabItem>
        </KebabMenu>
      </div>
      <p style={{ fontSize: 'var(--font-size-tiny)', fontWeight: 700, letterSpacing: 1.3, textTransform: 'uppercase', color: C.gold, margin: '0 0 9px', textAlign: 'center', whiteSpace: 'nowrap' }}>
        a passing thought · disappears in 24h
      </p>
      <p style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-lg)', fontStyle: 'italic', color: C.ink, lineHeight: 1.45, textAlign: 'center', margin: 'var(--space-none)' }}>
        "{item.text}"
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, justifyContent: 'center', marginTop: 'var(--space-10)' }}>
        <Avatar username={item.username} avatar_url={item.avatar_url} size={22} />
        <Handle username={item.username} />
      </div>
      <PostMeta postId={item.id} likeCount={item.like_count} commentCount={item.comment_count} onOpenComments={() => onOpenComments(item)} />
    </div>
  );
}

function SubstackCard({ item }) {
  const [opening, setOpening] = useState(false);
  const [latestTitle, setLatestTitle] = useState(null);
  const [fetchFailed, setFetchFailed] = useState(false);

  // Parsed once, up front. Everything below navigates to `target.href`, which
  // came out of a URL that already parsed — so nothing here can build the
  // unparseable `https://` that used to reach location.href and throw.
  const target = useMemo(() => parseNewsletterUrl(item.substack_url), [item.substack_url]);

  // Fetch the real latest post title on mount — the artifact shows actual
  // post titles, not a generic "New posts from @handle" placeholder.
  useEffect(() => {
    if (!target) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const res = await fetchWithTimeout(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/substack-rss-feed`, {
          method: 'POST',
          headers: bookSearchHeaders(session, SUPABASE_PUBLISHABLE_KEY),
          body: JSON.stringify({ rss_url: target.feed }),
        }, FEED_TIMEOUT_MS);
        const data = await res.json();
        if (!cancelled) setLatestTitle(data.items?.[0]?.title || null);
      } catch {
        if (!cancelled) setFetchFailed(true);
      }
    })();
    return () => { cancelled = true; };
  }, [item.substack_url, target]);

  async function openLatest() {
    if (!target) return;

    // Open the tab synchronously, right on click — browsers block window.open()
    // if it happens after an await, since it's no longer a direct result of
    // the user gesture. We redirect this already-open tab once we know the URL.
    const newTab = window.open('', '_blank');
    setOpening(true);

    // Their profile link is the floor. A post link only replaces it if the feed
    // gave us one that parses — the feed is third-party data, same as the
    // profile value, and neither gets assigned unchecked.
    let destination = target.href;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetchWithTimeout(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/substack-rss-feed`, {
        method: 'POST',
        headers: bookSearchHeaders(session, SUPABASE_PUBLISHABLE_KEY),
        body: JSON.stringify({ rss_url: target.feed }),
      }, FEED_TIMEOUT_MS);
      const data = await res.json();
      destination = safeHttpUrl(data.items?.[0]?.link) || target.href;
    } catch {
      // Keep the profile link. The old code retried the exact expression that
      // had just thrown, which is what turned a caught error into an unhandled
      // rejection.
    } finally {
      setOpening(false);
    }

    // `closed` matters: the blank tab is open across an await, and touching
    // location on a tab the user shut in the meantime throws in Safari.
    if (newTab && !newTab.closed) newTab.location.href = destination;
    else window.open(destination, '_blank'); // popup blocked even for the blank tab — last resort
  }

  // The feed filters these out, but a card already on screen when the value
  // changes shouldn't offer a button that goes nowhere.
  if (!target) return null;

  return (
    <div style={{
      background: '#fffdf8', border: `1px solid ${C.gold}66`, borderRadius: '26px 26px 14px 14px',
      boxShadow: '0 5px 20px rgba(45,74,62,0.07)', padding: '18px 20px 15px',
      breakInside: 'avoid', marginBottom: 22,
    }}>
      <p style={{ fontSize: 'var(--font-size-tiny)', fontWeight: 700, letterSpacing: 1.3, textTransform: 'uppercase', color: '#6b4c3b', margin: '0 0 9px', textAlign: 'center', whiteSpace: 'nowrap' }}>
        ✍️ from their {target.platform}
      </p>
      <p style={{ fontFamily: 'var(--font-display)', fontSize: 15, color: C.forest, lineHeight: 1.35, textAlign: 'center', margin: '0 0 10px' }}>
        {latestTitle || (fetchFailed ? `@${item.username}'s ${target.platform}` : '…')}
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', justifyContent: 'center' }}>
        <Avatar username={item.username} avatar_url={item.avatar_url} size={22} />
        <Handle username={item.username} />
        <button onClick={openLatest} disabled={opening}
          style={{ marginLeft: 'var(--space-6)', padding: '5px 15px', background: 'transparent', border: `1px solid ${C.forest}`, borderRadius: 'var(--radius-pill)', fontSize: 'var(--font-size-xs)', fontWeight: 700, color: C.forest, cursor: 'pointer' }}>
          {opening ? 'Opening…' : 'Read →'}
        </button>
      </div>
    </div>
  );
}

function CommentModal({ post, onClose }) {
  const { comments, loading, addComment } = useThoughtComments(post.id);
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);

  async function submit() {
    if (!draft.trim() || posting) return;
    setPosting(true);
    const { error } = await addComment(draft);
    if (error === 'not logged in') alert('Please log in to comment.');
    setDraft('');
    setPosting(false);
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 50, background: 'rgba(41,37,36,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-xxl)' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fffdf8', borderRadius: '26px 26px 16px 16px', border: `1px solid ${C.gold}`, width: '100%', maxWidth: 480, maxHeight: '82vh', overflowY: 'auto', boxShadow: '0 20px 50px rgba(0,0,0,0.35)' }}>
        <div style={{ padding: 18, borderLeft: `5px solid ${post.post_type === 'hot_take' ? C.coral : C.rust}`, borderBottom: '1px solid #f0e9dc' }}>
          <p style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, letterSpacing: 2.5, textTransform: 'uppercase', color: post.post_type === 'hot_take' ? C.coral : C.gold, margin: '0 0 6px' }}>
            {post.post_type === 'hot_take' ? '🔥 a hot take, served hot' : 'a passing thought'} · @{post.username}
          </p>
          <p style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontStyle: 'italic', lineHeight: 1.4, margin: 'var(--space-none)' }}>"{post.text}"</p>
        </div>
        <div style={{ padding: '14px 18px' }}>
          {loading && <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', textAlign: 'center', padding: '16px 0' }}>Loading…</p>}
          {!loading && comments.length === 0 && <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', textAlign: 'center', padding: '16px 0' }}>no replies yet, be the first 👀</p>}
          {comments.map((cm, i) => (
            <div key={cm.id} style={{ display: 'flex', gap: 'var(--space-sm)', padding: '9px 0', borderBottom: i < comments.length - 1 ? '1px solid var(--brand-cream)' : 'none' }}>
              <Avatar username={cm.username} avatar_url={cm.avatar_url} size={24} />
              <div>
                <Handle username={cm.username} color={C.ink} />
                <p style={{ fontSize: 12.5, color: 'var(--neutral-600)', margin: '1px 0 0' }}>{cm.content}</p>
              </div>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-sm)', padding: '12px 18px 18px', borderTop: '1px solid var(--brand-cream)', position: 'sticky', bottom: 0, background: '#fffdf8' }}>
          <input value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={e => e.key === 'Enter' && submit()}
            placeholder="add a reply…"
            style={{ flex: 1, padding: '10px 14px', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-pill)', fontSize: 'var(--font-size-base)', outline: 'none', background: 'var(--brand-white)' }} />
          <button onClick={submit} disabled={posting}
            style={{ padding: '10px 20px', background: C.forest, color: C.cream, border: 'none', borderRadius: 'var(--radius-pill)', fontSize: 'var(--font-size-sm)', fontWeight: 700, cursor: 'pointer' }}>
            Post
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ThoughtsTab() {
  const [filter, setFilter]       = useState('all');
  const [shown, setShown]         = useState(12);
  const [openPost, setOpenPost]   = useState(null);
  const [reportFor, setReportFor] = useState(null);
  const isMobile = useIsMobile();

  const { items: allItems, loading } = useThoughtsFeed();
  const items = filter === 'all' ? allItems : allItems.filter(i => i.post_type === filter);
  const visible = items.slice(0, shown);

  const counts = {
    all: allItems.length,
    hot_take: allItems.filter(i => i.post_type === 'hot_take').length,
    thought: allItems.filter(i => i.post_type === 'thought').length,
    substack: allItems.filter(i => i.post_type === 'substack').length,
  };

  function chip(active) {
    return {
      padding: isMobile ? '6px 12px' : '7px 15px', borderRadius: 'var(--radius-pill)', fontSize: isMobile ? 10.5 : 11.5, fontWeight: 700, letterSpacing: 0.5, cursor: 'pointer',
      background: active ? C.gold : 'transparent', color: active ? C.forest : C.forest,
      border: active ? `1px solid ${C.gold}` : `1px solid ${C.forest}44`,
      whiteSpace: 'nowrap', flexShrink: 0,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
    };
  }

  return (
    <div>
      <div className="rc-hide-scrollbar" style={{
        display: 'flex', gap: isMobile ? 7 : 9, justifyContent: isMobile ? 'flex-start' : 'center',
        marginBottom: 'var(--space-xxl)', flexWrap: isMobile ? 'nowrap' : 'wrap',
        overflowX: isMobile ? 'auto' : 'visible', WebkitOverflowScrolling: 'touch',
        padding: isMobile ? '0 2px 4px' : 0,
      }}>
        {[['all', `All${loading ? '' : ` · ${counts.all}`}`], ['hot_take', `🔥 Hot Takes${loading ? '' : ` · ${counts.hot_take}`}`], ['thought', `💭 Thoughts${loading ? '' : ` · ${counts.thought}`}`], ['substack', `✍️ Newsletters${loading ? '' : ` · ${counts.substack}`}`]].map(([k, l]) => (
          <button key={k} onClick={() => { setFilter(k); setShown(12); }} style={chip(filter === k)}>{l}</button>
        ))}
      </div>

      {loading && items.length === 0 && (
        <p style={{ textAlign: 'center', color: C.grey, fontSize: 'var(--font-size-base)' }}>Loading…</p>
      )}

      {!loading && items.length === 0 && (
        <p style={{ textAlign: 'center', color: C.grey, fontSize: 'var(--font-size-base)', fontStyle: 'italic' }}>
          Nothing here yet, be the first to share a thought.
        </p>
      )}

      <div style={{ columns: isMobile ? 1 : 3, columnGap: 22 }}>
        {visible.map(item => {
          const key = `${item.post_type}-${item.id || item.user_id}`;
          if (item.post_type === 'hot_take') return <HotTakeCard key={key} item={item} onReport={setReportFor} onOpenComments={setOpenPost} />;
          if (item.post_type === 'thought')  return <ThoughtCard  key={key} item={item} onReport={setReportFor} onOpenComments={setOpenPost} />;
          return <SubstackCard key={key} item={item} />;
        })}
      </div>

      {shown < items.length && (
        <div style={{ textAlign: 'center', marginTop: 26 }}>
          <button onClick={() => setShown(n => n + 12)}
            style={{ padding: '11px 34px', background: 'transparent', border: `1.5px solid ${C.forest}`, borderRadius: 'var(--radius-pill)', fontSize: 12.5, fontWeight: 700, letterSpacing: 1, color: C.forest, cursor: 'pointer' }}>
            Load more · {items.length - shown} left
          </button>
        </div>
      )}

      {openPost && <CommentModal post={openPost} onClose={() => setOpenPost(null)} />}

      {reportFor && (
        <ReportModal
          entityType="post"
          entityId={reportFor.id}
          reportedUserId={reportFor.user_id}
          onClose={() => setReportFor(null)}
        />
      )}
    </div>
  );
}